import type { PaginationModel } from '@montugroup/design-system';
import type { SortingState } from '@tanstack/react-table';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getTsWarehousingConsignmentsClient } from '@/api/warehousing-consignments.client';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import useGetPharmacistShipments from '@/hooks/useGetPharmacistShipments';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import { noOp } from '@montugroup/circuit-shared/utils/noOp';
import type { DispatchedConsignmentStatus, ListConsignmentDto } from '@montugroup/contracts-warehousing-api';
import { useQuery } from '@tanstack/react-query';

const DEFAULT_PAGE_SIZE = 50;
const INITIAL_PAGE = 0;

const {
  listConsignments: { keys },
} = getTsWarehousingConsignmentsClient();

export type PharmacistShipmentsData = {
  pageSize: number;
  handlePaginationModelChange: (model: PaginationModel) => void;
  handleSortingOrderChange: (sortOrder: SortingState) => void;
  referenceFilter: string;
  setReferenceFilter: (referenceFilter: string) => void;
  shipments: ListConsignmentDto[];
  loading: boolean;
  statusFilter: DispatchedConsignmentStatus[];
  setStatusFilter: (statusFilter: DispatchedConsignmentStatus[]) => void;
  shouldResetPageIndex?: boolean;
  setShouldResetPageIndex: (value: boolean) => void;
  beforeCursor: string | null;
  afterCursor: string | null;
};

export const PharmacistShipmentsContext = createContext<PharmacistShipmentsData>({
  pageSize: DEFAULT_PAGE_SIZE,
  handlePaginationModelChange: noOp,
  handleSortingOrderChange: noOp,
  referenceFilter: '',
  setReferenceFilter: noOp,
  shipments: [],
  loading: false,
  statusFilter: [],
  setStatusFilter: noOp,
  shouldResetPageIndex: false,
  setShouldResetPageIndex: noOp,
  beforeCursor: null,
  afterCursor: null,
});

export function PharmacistShipmentsProvider(props: PropsWithChildren) {
  const { children } = props;
  const {
    paginationModel: { pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({
    page: INITIAL_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder();
  const [referenceFilter, setReferenceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<DispatchedConsignmentStatus[]>(['IN_TRANSIT', 'RECEIVED']);
  const [beforeCursor, setBeforeCursor] = useState<string | null>(null);
  const [afterCursor, setAfterCursor] = useState<string | null>(null);

  const [searchCursor, setSearchCursor] = useState<{ after?: string; before?: string } | null>(null);

  const { data, mutateAsync, isPending: isShipmentsLoading } = useGetPharmacistShipments();

  const { data: shipmentData } = useQuery<typeof data>({
    queryKey: [
      ...keys,
      referenceFilter,
      statusFilter,
      searchCursor?.after,
      searchCursor?.before,
      pageSize,
      sortingOrder.name,
      sortingOrder.reverse,
    ],
    queryFn: async () => {
      return mutateAsync({
        body: {
          filter: {
            status: statusFilter,
            ...(referenceFilter ? { reference: referenceFilter } : {}),
          },
          page: {
            size: pageSize,
            ...searchCursor,
          },
          ...(['status', 'dispatchedDate'].includes(sortingOrder.name)
            ? {
                sort: [
                  {
                    field: sortingOrder.name as 'status' | 'dispatchedDate',
                    direction: sortingOrder.reverse ? 'desc' : 'asc',
                  },
                ],
              }
            : {}),
        },
        headers: { authorization: getAuthBearerToken() },
      });
    },
  });

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => {
      if (model.pageSize !== pageSize) {
        setShouldResetPageIndex(true);
        setSearchCursor(null);
      }
      if (model.page > 0 && afterCursor) {
        setSearchCursor({ after: afterCursor });
      } else if (model.page < 0 && beforeCursor) {
        setSearchCursor({ before: beforeCursor });
      }

      setPaginationModel(model);
    },
    [setPaginationModel, setShouldResetPageIndex, pageSize, afterCursor, beforeCursor],
  );

  useEffect(() => {
    const { next, prev } = shipmentData?.body.meta.page?.cursor ?? {};
    setBeforeCursor(prev ?? null);
    setAfterCursor(next ?? null);
  }, [shipmentData?.body.meta.page?.cursor]);

  useEffect(() => {
    setShouldResetPageIndex(true);
    setSearchCursor(null);
  }, [referenceFilter, statusFilter, sortingOrder, setShouldResetPageIndex]);

  const value = useMemo(
    () => ({
      pageSize,
      shipments: shipmentData?.body.data ?? [],
      loading: isShipmentsLoading,
      referenceFilter,
      setReferenceFilter,
      statusFilter,
      setStatusFilter,
      handlePaginationModelChange,
      handleSortingOrderChange,
      shouldResetPageIndex,
      setShouldResetPageIndex,
      beforeCursor,
      afterCursor,
    }),
    [
      pageSize,
      shipmentData?.body.data,
      isShipmentsLoading,
      referenceFilter,
      statusFilter,
      handlePaginationModelChange,
      handleSortingOrderChange,
      shouldResetPageIndex,
      setShouldResetPageIndex,
      beforeCursor,
      afterCursor,
    ],
  );

  return <PharmacistShipmentsContext.Provider value={value}>{children}</PharmacistShipmentsContext.Provider>;
}

export const usePharmacistShipments = () => {
  const {
    pageSize,
    shipments,
    loading,
    referenceFilter,
    statusFilter,
    setReferenceFilter,
    handlePaginationModelChange,
    handleSortingOrderChange,
    setStatusFilter,
    shouldResetPageIndex,
    setShouldResetPageIndex,
    beforeCursor,
    afterCursor,
  } = useContext(PharmacistShipmentsContext);

  const clearAllFilters = () => {};

  const setFilter = (filter: string) => {
    setReferenceFilter(filter || '');
  };

  return {
    pageSize,
    shipments,
    referenceFilter,
    statusFilter,
    loading,
    setFilter,
    clearAllFilters,
    setReferenceFilter,
    handlePaginationModelChange,
    handleSortingOrderChange,
    setStatusFilter,
    shouldResetPageIndex,
    setShouldResetPageIndex,
    beforeCursor,
    afterCursor,
  };
};
